import { useState } from 'react'

import { EMPTY_LIST } from '../../../../../constants/common'
import {
  SUBMENU_DEFAULT_VALUE,
  SUBMENU_PARAM_ARCHIVE_VALUE,
} from '../../../../../constants/route'
import { useSelection } from '../../../../../hooks/useSelection'
import {
  CONTRACT_REQUIRED_COLUMNS,
  MOCK_CONTRACTS_PAGE_SIZE,
} from '../ProfileContractsTabInhabitant.constants'
import {
  MOCK_CONTRACTS,
  MOCK_CONTRACTS_PERMISSIONS,
} from '../ProfileContractsTabInhabitant.mock'

import {
  getContractsColumns,
  getContractsForSubTab,
  getPagedContracts,
  getSortedContracts,
} from './useContractsMockTable.utils'

/**
 * Local, no-network stand-in for the backend-driven `useAsyncTable`, powering the
 * /library Contracts-tab demo. Holds the table state (columns, ordering,
 * pagination, selection) and serves rows from {@link MOCK_CONTRACTS} with
 * client-side sub-tab filter, sort and pagination. The production tab uses
 * `useEntityTabContractsTable` (real endpoints) instead.
 */
export function useContractsMockTable({ sub, viewerRole }) {
  const [columns, setColumns] = useState(() => getContractsColumns(viewerRole))
  const [ordering, setOrdering] = useState(EMPTY_LIST)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(MOCK_CONTRACTS_PAGE_SIZE)
  const [isConfigOpen, setConfigOpen] = useState(false)
  const [prevSub, setPrevSub] = useState(sub)

  const allCount = getContractsForSubTab(
    MOCK_CONTRACTS,
    SUBMENU_DEFAULT_VALUE
  ).length
  const archiveCount = getContractsForSubTab(
    MOCK_CONTRACTS,
    SUBMENU_PARAM_ARCHIVE_VALUE
  ).length

  const sortedContracts = getSortedContracts(
    getContractsForSubTab(MOCK_CONTRACTS, sub),
    ordering
  )

  const count = sortedContracts.length
  const pageCount = Math.max(1, Math.ceil(count / pageSize))
  const safePage = Math.min(page, pageCount)

  const items = {
    items: getPagedContracts(sortedContracts, { page: safePage, pageSize }),
    permissions: MOCK_CONTRACTS_PERMISSIONS,
  }

  const pagination = { count, page: safePage, pageCount, pageSize }

  const selection = useSelection({ data: items, pagination })

  // Reset paging and clear selection when the sub-tab (All / Archive) changes.
  // Render-time reset (React's "adjust state when a prop changes" pattern) rather
  // than an effect, so we don't trigger cascading renders.
  if (sub !== prevSub) {
    setPrevSub(sub)
    setPage(1)
    selection.handleCleanSelection()
  }

  async function handleSetOrdering(nextOrdering) {
    setOrdering(nextOrdering)
    setPage(1)

    return nextOrdering
  }

  async function handleResetOrdering() {
    return handleSetOrdering(EMPTY_LIST)
  }

  function handleSetPagination(nextPagination) {
    setPage(nextPagination.page)
  }

  function handleSetSize(nextSize) {
    setPageSize(nextSize)
    setPage(1)
  }

  async function handleRefresh() {
    selection.handleCleanSelection()
  }

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  // Committing column settings is local; the live `columns` state already
  // reflects the toggles, so apply is a resolved no-op.
  async function handleApply() {}

  return {
    ...selection,
    allCount,
    archiveCount,
    columns,
    handleApply,
    handleConfigOpen,
    handleRefresh,
    handleResetOrdering,
    handleSetColumns: setColumns,
    handleSetOrdering,
    handleSetPagination,
    handleSetSize,
    isConfigOpen,
    isLoading: false,
    items,
    ordering,
    pagination,
    permissions: MOCK_CONTRACTS_PERMISSIONS,
    requiredColumns: CONTRACT_REQUIRED_COLUMNS,
    setConfigOpen,
  }
}
